/**
 * AG-07 Founder Copilot · Chat UI real (no placeholder).
 *
 * Backend: /api/founder-os/copilot/{index,status,chat,sessions/{id}}
 * - RAG vectorial sobre 17+ markdowns en /app/memory
 * - LIVE DATA pre-fetched (saas_metrics, funnel_30d, ai_activity, audits,
 *   security_checklist)
 * - Historial conversacional persistido en Mongo
 * - Citas obligatorias con score
 *
 * Restricción del founder: 0 inventos · 0 placeholders.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Robot, PaperPlaneTilt, Database, ArrowsClockwise, Trash, Plus, Sparkle,
  CheckCircle, Warning, FileText, Lightning, Download, BookOpen,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";

const SESSION_KEY = "ag:copilot:session_id";

const SUGGESTIONS = [
  "¿Cuál es el estado real de los 7 agentes IA?",
  "Dame MRR, ARR, churn y módulos que generan ingresos hoy.",
  "¿Qué módulos están realmente implementados vs mockup?",
  "¿Qué riesgos críticos hay según los audits?",
  "¿Qué sprint sigue y por qué?",
  "¿Qué prioridades P0 tengo pendientes?",
];

export default function Copilot() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [reindexing, setReindexing] = useState(false);
  const [loadingBaked, setLoadingBaked] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  // Cargar status + historial inicial
  useEffect(() => { loadStatus(); }, []);
  useEffect(() => {
    if (sessionId) loadSession(sessionId);
  }, [sessionId]);
  useEffect(() => {
    // Auto-scroll al final
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Polling automático cuando hay job en curso
  useEffect(() => {
    const running = status?.indexing_job?.running;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(loadStatus, 3000);
    }
    if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      // Si terminó hace poco, refrescar una vez más para asegurar
      if (status?.indexing_job?.finished_at) setReindexing(false);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status?.indexing_job?.running, status?.indexing_job?.finished_at]);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/founder-os/copilot/status");
      setStatus(data);
    } catch (e) {
      console.error("[copilot] status fail:", e);
    } finally { setStatusLoading(false); }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/founder-os/copilot/sessions/${sid}`);
      if (data?.messages) setMessages(data.messages);
    } catch (e) {
      console.warn("[copilot] no session history:", e?.response?.status);
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setError(null);
    setSending(true);
    // Optimistic UI
    const userMsg = { role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "…", _pending: true }]);
    setInput("");
    try {
      const { data } = await api.post("/founder-os/copilot/chat", {
        message: msg,
        session_id: sessionId,
        top_k: 6,
      });
      const assistantMsg = {
        role: "assistant",
        content: data.response,
        citations: data.citations || [],
        live_data_snapshot_at: data.live_data?.snapshot_at,
        live_data: data.live_data,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => prev.filter((m) => !m._pending).concat(assistantMsg));
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      setError(detail);
      setMessages((prev) => prev.filter((m) => !m._pending));
    } finally {
      setSending(false);
    }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    setError(null);
  };

  const clearSession = async () => {
    if (!sessionId) return newSession();
    if (!window.confirm("¿Borrar la conversación actual?")) return;
    try {
      await api.delete(`/founder-os/copilot/sessions/${sessionId}`);
    } catch (e) { /* ignore */ }
    newSession();
  };

  const reindex = async () => {
    if (reindexing || status?.indexing_job?.running) return;
    if (!window.confirm("Reindexar toda la Knowledge Base?\n\nRecomputa chunks + embeddings desde Mongo. Si el embedder no carga, indexará solo BM25 (sigue funcional).\n\nEl proceso corre en background.")) return;
    setReindexing(true);
    setError(null);
    try {
      const { data } = await api.post("/founder-os/copilot/index");
      if (data.already_running) {
        await loadStatus();
      } else {
        await loadStatus();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      setError(detail);
      setReindexing(false);
    }
  };

  const loadBaked = async () => {
    if (loadingBaked) return;
    if (!window.confirm("Cargar KB pre-baked en Mongo?\n\nCarga 1565 chunks con embeddings desde el snapshot bundled en el código. Instantáneo · sin descargas externas.\n\nUsar si la KB está vacía tras un deploy.")) return;
    setLoadingBaked(true);
    setError(null);
    try {
      const { data } = await api.post("/founder-os/copilot/load-baked");
      if (data.loaded) {
        await loadStatus();
      } else {
        setError(`Baked no cargado: ${data.reason || "razón desconocida"}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      setError(detail);
    } finally {
      setLoadingBaked(false);
    }
  };

  const ready = status?.ready === true;
  const mode = status?.mode || (status?.chunks_with_embeddings > 0 ? "hybrid" : "bm25_only");
  const overall = status?.overall || (ready ? "healthy" : "critical");

  return (
    <div className="space-y-4" data-testid="copilot-root">
      {/* Header con stats */}
      <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--ag-gold)]/15 border border-[var(--ag-gold)]/30 flex items-center justify-center">
              <Robot size={20} weight="fill" className="text-[var(--ag-gold)]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold">AG-07 · Founder Copilot</div>
              <div className="text-sm text-[var(--ag-text)] font-medium">CTO · COO · Product Manager de AutoGlow</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {statusLoading ? (
              <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
            ) : overall === "healthy" ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="copilot-status-ready">
                <CheckCircle size={11} weight="fill" /> KB · {status.total_chunks} chunks · {mode === "hybrid" ? "Híbrido" : "BM25"}
              </span>
            ) : overall === "degraded" ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="copilot-status-degraded">
                <Warning size={11} weight="fill" /> KB · {status.total_chunks} chunks · BM25-only
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm bg-red-500/15 text-red-400 border border-red-500/30" data-testid="copilot-status-empty">
                <Warning size={11} weight="fill" /> KB vacía
              </span>
            )}
            {!ready && (
              <button
                onClick={loadBaked}
                disabled={loadingBaked}
                className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 hover:bg-[var(--ag-gold)]/20 text-[var(--ag-gold)] disabled:opacity-50 font-semibold"
                data-testid="copilot-load-baked"
                title="Cargar pre-baked embeddings · instant"
              >
                <Download size={12} className={loadingBaked ? "animate-pulse" : ""} /> {loadingBaked ? "Cargando…" : "Cargar baked"}
              </button>
            )}
            <Link
              to="/founder/intelligence?sub=kb-manager"
              className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              data-testid="copilot-kb-manager-link"
              title="Gestionar KB · upload markdown"
            >
              <BookOpen size={12} /> KB Manager
            </Link>
            <button
              onClick={reindex}
              disabled={reindexing}
              className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] disabled:opacity-50"
              data-testid="copilot-reindex"
              title="Re-indexar Knowledge Base desde Mongo"
            >
              <ArrowsClockwise size={12} className={reindexing ? "animate-spin" : ""} /> {reindexing ? "Indexando…" : "Reindexar"}
            </button>
            <button
              onClick={newSession}
              className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[var(--ag-border)] hover:bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              data-testid="copilot-new-session"
              title="Nueva conversación"
            >
              <Plus size={12} /> Nueva
            </button>
            {sessionId && (
              <button
                onClick={clearSession}
                className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-red-500/30 hover:bg-red-500/10 text-red-400"
                data-testid="copilot-clear-session"
                title="Borrar conversación"
              >
                <Trash size={12} />
              </button>
            )}
          </div>
        </div>
        {status && (
          <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-[var(--ag-text-dim)]">
            <span className="inline-flex items-center gap-1"><Database size={10} /> {status.sources?.length || 0} documentos · {status.chunks_with_embeddings || 0}/{status.total_chunks || 0} con embeddings</span>
            <span>•</span>
            <span>modo: <code className="text-[var(--ag-text-muted)]">{mode === "hybrid" ? "BM25 + Embeddings" : "BM25-only"}</code></span>
            <span>•</span>
            <span>modelo: <code className="text-[var(--ag-text-muted)]">{status.embed_model?.split("/").pop()}</code></span>
            {sessionId && <><span>•</span><span>sesión: <code className="text-[var(--ag-text-muted)]">{sessionId.slice(0, 16)}</code></span></>}
          </div>
        )}

        {/* Progreso del job de indexación en background */}
        {status?.indexing_job?.running && (
          <div className="mt-3 p-3 rounded-sm bg-[var(--ag-gold)]/5 border border-[var(--ag-gold)]/30" data-testid="copilot-index-progress">
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-[var(--ag-gold)]">
                <ArrowsClockwise size={12} className="animate-spin" />
                <strong>Indexando KB en background…</strong>
              </div>
              <div className="text-[10px] text-[var(--ag-text-dim)] tabular-nums">
                {status.indexing_job.files_done}/{status.indexing_job.files_total || "?"} archivos · {status.indexing_job.chunks_total} chunks
              </div>
            </div>
            <div className="mt-2 text-[10px] text-[var(--ag-text-muted)]">
              Actual: <code>{status.indexing_job.current_file || "preparando…"}</code>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-[var(--ag-bg)] overflow-hidden">
              <div className="h-full bg-[var(--ag-gold)] transition-all" style={{
                width: status.indexing_job.files_total > 0
                  ? `${Math.round((status.indexing_job.files_done / status.indexing_job.files_total) * 100)}%`
                  : "10%",
              }} />
            </div>
          </div>
        )}
        {!status?.indexing_job?.running && status?.indexing_job?.last_error && (
          <div className="mt-3 p-2 rounded-sm bg-red-500/10 border border-red-500/30 text-[11px] text-red-400">
            Último error de indexación: {status.indexing_job.last_error}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-sm border border-red-500/30 bg-red-500/10 text-red-400 text-xs px-3 py-2" data-testid="copilot-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Chat area */}
      <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] overflow-hidden flex flex-col" style={{ minHeight: 480 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="copilot-messages" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 ? (
            <EmptyState ready={ready} onPick={(q) => setInput(q)} />
          ) : (
            messages.map((m, i) => (
              <MessageBubble key={i} msg={m} />
            ))
          )}
        </div>

        {/* Input */}
        <div className="border-t border-[var(--ag-border)] p-3 bg-[var(--ag-midnight)]/20">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={sending || !ready}
              placeholder={ready ? "Pregunta a AG-07… (Enter = enviar, Shift+Enter = nueva línea)" : "Indexa la KB para empezar"}
              rows={2}
              className="flex-1 resize-none px-3 py-2 rounded-sm bg-[var(--ag-bg)] border border-[var(--ag-border)] focus:border-[var(--ag-gold)] outline-none text-sm text-[var(--ag-text)] disabled:opacity-50"
              data-testid="copilot-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim() || !ready}
              className="px-3 py-2.5 rounded-sm bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
              data-testid="copilot-send"
            >
              <PaperPlaneTilt size={15} weight="fill" />
              <span className="hidden sm:inline">Enviar</span>
            </button>
          </div>
          <div className="text-[10px] text-[var(--ag-text-muted)] mt-1.5 px-1">
            AG-07 responde solo con datos de la KB indexada + LIVE_DATA de Mongo. No inventa. Cita todas las fuentes.
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ ready, onPick }) {
  return (
    <div className="text-center py-8" data-testid="copilot-empty">
      <Robot size={48} weight="duotone" className="mx-auto text-[var(--ag-gold)]/60" />
      <h3 className="font-display text-xl font-bold tracking-tighter mt-3">¿Qué necesitas saber, founder?</h3>
      <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-md mx-auto">
        AG-07 puede responder cualquier pregunta sobre el estado de AutoGlow:
        métricas live, módulos implementados vs mockup, prioridades, riesgos, sprint siguiente.
      </p>
      {ready ? (
        <div className="mt-5 flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              onClick={() => onPick(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/40 hover:bg-[var(--ag-gold)]/5 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] transition-colors"
              data-testid={`copilot-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-4 text-xs text-amber-400 space-y-2">
          <div>Antes de usar AG-07 hay que cargar la Knowledge Base.</div>
          <div className="text-[var(--ag-text-dim)]">Recomendado: pulsa <strong>«Cargar baked»</strong> arriba (instantáneo, 1565 chunks pre-computados). Alternativa: «Reindexar» recomputa desde Mongo.</div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const isPending = msg._pending;
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`} data-testid={`copilot-msg-${msg.role}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-[var(--ag-gold)]/15 border border-[var(--ag-gold)]/30 flex items-center justify-center shrink-0">
          <Robot size={14} weight="fill" className="text-[var(--ag-gold)]" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className={`rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-[var(--ag-gold)]/15 border border-[var(--ag-gold)]/30 text-[var(--ag-text)]"
            : "bg-[var(--ag-midnight)]/40 border border-[var(--ag-border)] text-[var(--ag-text)]"
        }`}>
          {isPending ? (
            <span className="inline-flex items-center gap-2 text-[var(--ag-text-dim)]">
              <Sparkle size={12} className="animate-pulse text-[var(--ag-gold)]" />
              AG-07 está consultando la KB y los datos live…
            </span>
          ) : (
            msg.content
          )}
        </div>
        {/* Citations + live data summary */}
        {!isUser && !isPending && msg.citations && msg.citations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="copilot-citations">
            {msg.citations.map((c, i) => (
              <a
                key={i}
                href={`/founder/intelligence?sub=knowledge&doc=${encodeURIComponent(c.source)}`}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm bg-[var(--ag-bg)] border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/40 text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
                title={`Score: ${c.score} · chunk ${c.chunk_idx}`}
                data-testid={`copilot-citation-${i}`}
              >
                <FileText size={9} /> {c.source.replace(".md", "")} · {c.score}
              </a>
            ))}
          </div>
        )}
        {!isUser && !isPending && msg.live_data && (
          <details className="text-[10px] text-[var(--ag-text-muted)] mt-1">
            <summary className="cursor-pointer hover:text-[var(--ag-gold)] inline-flex items-center gap-1">
              <Lightning size={10} /> live_data
            </summary>
            <pre className="mt-1 p-2 rounded-sm bg-[var(--ag-bg)] border border-[var(--ag-border)] overflow-x-auto" style={{ maxWidth: 600 }}>
{JSON.stringify(msg.live_data, null, 2)}
            </pre>
          </details>
        )}
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-full bg-[var(--ag-bg)] border border-[var(--ag-border)] flex items-center justify-center shrink-0 text-[10px] font-bold text-[var(--ag-gold)]">YO</div>
      )}
    </div>
  );
}
